import classNames from "classnames";
import React, { useMemo } from "react";
import { Container, Dropdown } from "react-bootstrap";
import { Option } from "react-select/src/filters";
import { Icon, IconName } from "@/components";
import { PartnerBookingPolicy, PartnerBookingHour } from "@/data/partners";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { Spacer } from "@/offering/spacer";
import { MeetingRoomsFilter } from "@/offering/template_offering_listing_meeting_room";
import styles from "./desktop_filters.module.css";
import { FilterId, MobileFilter } from "./mobile_filters";
import { DesktopDateTimeFilterItem } from "./desktop_date_time_filter_item";

interface DesktopFiltersProps {
  filters: MobileFilter;
  filterValues: MeetingRoomsFilter;
  onFilterValuesChange: (filter: MeetingRoomsFilter) => void;
  partnerBookingPolicy?: PartnerBookingPolicy;
  partnerBookingHour?: PartnerBookingHour;
  spacesAvailableText?: string;
}

export function DesktopFilters({
  filters,
  filterValues,
  onFilterValuesChange,
  partnerBookingHour,
  partnerBookingPolicy,
  spacesAvailableText,
}: DesktopFiltersProps) {
  const { fbt } = useI18n();
  const locationOptions = useMemo(() => {
    let filteredLocations = filters.location?.options || [];
    if (filterValues.state) {
      filteredLocations = filteredLocations.filter(
        (o) => o.data.state === filterValues.state
      );
    }
    if (filterValues.city) {
      filteredLocations = filteredLocations.filter(
        (o) => o.data.city === filterValues.city
      );
    }
    return filteredLocations;
  }, [filters, filterValues]);

  const filterKeys = Object.keys(filters) as FilterId[];
  const numberOfFilters = filterKeys.length;

  if (numberOfFilters === 0) {
    return <></>;
  }

  return (
    <div className={`${styles.container} mr-gallery-desktop-filters`}>
      <Container className="d-flex align-items-center justify-content-between">
        <div className={styles.filtersWrapper}>
          {filters.state && (
            <DesktopFilterItem
              iconName={filters.state.iconName}
              value={filters.state.value}
              onSelect={filters.state.onChange}
              options={filters.state.options}
              label={filters.state.label}
              testID="state"
            />
          )}
          {filters.city && (
            <DesktopFilterItem
              iconName={filters.city.iconName}
              value={filters.city.value}
              onSelect={filters.city.onChange}
              options={filters.city.options}
              label={filters.city.label}
              placeholder={fbt("Any city")}
              testID="city"
            />
          )}
          {filters.location && (
            <DesktopFilterItem
              iconName={filters.location.iconName}
              value={filters.location.value}
              onSelect={filters.location.onChange}
              options={locationOptions}
              placeholder={fbt("Any location")}
              label={filters.location.label}
              testID="location"
            />
          )}
          {filters.layout && (
            <DesktopFilterItem
              iconName={filters.layout.iconName}
              value={filters.layout.value}
              onSelect={filters.layout.onChange}
              options={filters.layout.options}
              label={filters.layout.label}
              testID="layout"
            />
          )}
          {filters.capacity && (
            <DesktopFilterItem
              iconName={filters.capacity.iconName}
              value={filters.capacity.value}
              onSelect={filters.capacity.onChange}
              options={filters.capacity.options}
              label={filters.capacity.label}
              testID="capacity"
            />
          )}
          {filters.dateTime && (
            <DesktopDateTimeFilterItem
              iconName={filters.dateTime.iconName}
              value={filterValues}
              onChange={onFilterValuesChange}
              label={filters.dateTime.label}
              openAt={partnerBookingHour?.openAt}
              closedAt={partnerBookingHour?.closedAt}
              timeSlotIncrement={partnerBookingPolicy?.timeSlotIncrement}
            />
          )}
        </div>
        {spacesAvailableText && (
          <div>
            <span className="fw-bold">{spacesAvailableText}</span>{" "}
            <Icon name={"info"} size={16} />
          </div>
        )}
      </Container>
    </div>
  );
}

interface DesktopFilterItemProps {
  iconName: IconName;
  value?: string;
  label: string;
  placeholder?: string;
  onSelect: (value?: string) => void;
  options: Option[];
  testID?: string;
}

export function DesktopFilterItem(props: DesktopFilterItemProps) {
  const { fbt } = useI18n();
  const {
    iconName,
    value,
    placeholder = fbt("Any"),
    label,
    onSelect,
    options,
    testID,
  } = props;

  return (
    <div className={styles.dropdownWrapper}>
      <Dropdown
        className={classNames("checkout-box-picker", styles.dropdown)}
        onSelect={(value) => onSelect(value || undefined)}
      >
        <Dropdown.Toggle className={styles.dropdownToggle}>
          <Icon name={iconName} />
          <Spacer size={8} direction="horizontal" />
          <b>{label}:</b>
          <Spacer size={4} direction="horizontal" />
          {value ? (
            <div data-testid={`${testID}-value`}>
              {options.find((o) => o.value === value)?.label}
            </div>
          ) : (
            <div className={styles.placeholder}>{placeholder}</div>
          )}
          <Spacer size={32} direction="horizontal" />
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        {value && (
          <div className={styles.clearBtn} onClick={() => onSelect(undefined)}>
            <Icon name="go-x-circle" color="#82808E" size={16} />
          </div>
        )}
        <Dropdown.Menu className={styles.dropdownMenu}>
          {options.map((option) => (
            <Dropdown.Item
              key={option.value}
              eventKey={option.value}
              active={option.value === value}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
